/* global React, ReactDOM, Icon, ActionBtn */
const { useState, useEffect, useRef } = React;

const HOTSPOT_COPY = {
  en: {
    pass: 'Your Pass',
    mapLocation: 'Map Location',
    openMap: 'Open Map',
    placeTitle: 'Discover this place',
    placeSubtitle: 'More information, services, location, photos & more',
    close: 'Close',
    placeLoading: 'Loading…',
    placeError: "This page couldn't be loaded.",
    retry: 'Retry',
  },
  es: {
    pass: 'Tu Pase',
    mapLocation: 'Ubicacion en mapa',
    openMap: 'Abrir mapa',
    placeTitle: 'Descubre este lugar',
    placeSubtitle: 'Más información, servicios, ubicación, fotos y más',
    close: 'Cerrar',
    placeLoading: 'Cargando…',
    placeError: 'No se pudo cargar la ficha.',
    retry: 'Reintentar',
  },
};

function escapeHotspotHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function sanitizeHotspotDescriptionHtml(rawHtml) {
  const original = String(rawHtml ?? '');
  if (!original.trim()) return '';

  if (typeof DOMParser === 'undefined') {
    return escapeHotspotHtml(original).replace(/\r?\n/g, '<br>');
  }

  const hasMarkup = /<[^>]+>/.test(original);
  if (!hasMarkup) {
    return escapeHotspotHtml(original).replace(/\r?\n/g, '<br>');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${original}</div>`, 'text/html');
  const wrapper = doc.body.firstElementChild;
  if (!wrapper) return '';

  const allowedTags = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'BR', 'P', 'DIV', 'UL', 'OL', 'LI', 'A', 'BLOCKQUOTE', 'PRE']);
  const safeHrefPattern = /^(https?:|mailto:|tel:)/i;

  const sanitizeNode = (node) => {
    if (node.nodeType === 3) return doc.createTextNode(node.textContent || '');
    if (node.nodeType !== 1) return null;

    const tagName = node.tagName.toUpperCase();
    if (['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED'].includes(tagName)) return null;

    if (!allowedTags.has(tagName)) {
      const fragment = doc.createDocumentFragment();
      node.childNodes.forEach((child) => {
        const sanitizedChild = sanitizeNode(child);
        if (sanitizedChild) fragment.appendChild(sanitizedChild);
      });
      return fragment;
    }

    const safeEl = doc.createElement(tagName === 'DIV' ? 'p' : tagName.toLowerCase());
    if (tagName === 'A') {
      const href = String(node.getAttribute('href') || '').trim();
      if (href && safeHrefPattern.test(href)) {
        safeEl.setAttribute('href', href);
        safeEl.setAttribute('target', '_blank');
        safeEl.setAttribute('rel', 'noopener noreferrer');
      }
    }

    node.childNodes.forEach((child) => {
      const sanitizedChild = sanitizeNode(child);
      if (sanitizedChild) safeEl.appendChild(sanitizedChild);
    });

    if (tagName === 'A' && !safeEl.getAttribute('href')) {
      const fragment = doc.createDocumentFragment();
      while (safeEl.firstChild) fragment.appendChild(safeEl.firstChild);
      return fragment;
    }

    return safeEl;
  };

  const output = doc.createElement('div');
  wrapper.childNodes.forEach((child) => {
    const sanitizedChild = sanitizeNode(child);
    if (sanitizedChild) output.appendChild(sanitizedChild);
  });

  return output.innerHTML.replace(/\u00a0/g, ' ');
}

function stripHotspotDescriptionText(rawHtml) {
  const html = sanitizeHotspotDescriptionHtml(rawHtml);
  if (!html) return '';
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || '').replace(/\s+/g, ' ').trim();
}

function getLocalizedHotspotDescription(hs, lang) {
  const english = String(hs?.descEn || '').trim();
  if (lang === 'en' && english) return english;
  return hs?.desc || '';
}

function getLocalizedHotspotName(hs, lang) {
  const english = String(hs?.nameEn || '').trim();
  if (lang === 'en' && english) return english;
  return hs?.name || '';
}

// ---------- Wallet-style discount card ----------
function WalletCard({ data, peek, onOpen, expanded }) {
  if (!data) return null;
  const stamps = data.stamps || 0;
  const max = data.stampsMax || 0;
  const color = data.color || "#0061b4";
  const bg = `linear-gradient(135deg, ${color} 0%, ${shade(color, -0.25)} 100%)`;

  return (
    <div
      className="wallet-card"
      style={{
        background: bg,
        transform: peek
          ? "translateY(0)"
          : expanded ? "translateY(0)" : "translateY(0)",
      }}
      onClick={onOpen}
    >
      <div className="wc-bg" />
      <div className="wc-row1">
        <div className="wc-brand">CRMAPS · PASS</div>
        <div className="wc-mark">CR</div>
      </div>
      <div className="wc-title">{data.title}</div>
      <div className="wc-sub">{data.subtitle || data.offer}</div>
      <div className="wc-bottom">
        {max > 0 ? (
          <div className="wc-stamps">
            {Array.from({ length: max }).map((_, i) => (
              <div key={i} className={"wc-stamp" + (i < stamps ? " is-on" : "")} />
            ))}
          </div>
        ) : (
          <div className="wc-code">{data.code || "TAP TO REDEEM"}</div>
        )}
        {data.offer && max > 0 && (
          <div className="wc-code" style={{ opacity: 0.95 }}>{data.offer}</div>
        )}
        {!data.offer && max === 0 && data.code && (
          <span style={{ fontSize: 10, opacity: 0.85, fontWeight: 700, letterSpacing: "0.1em" }}>SAVED</span>
        )}
      </div>
    </div>
  );
}

function shade(hex, p) {
  // simple shade utility — p in [-1, 1]
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0,2), 16);
  const g = parseInt(c.slice(2,4), 16);
  const b = parseInt(c.slice(4,6), 16);
  const f = (v) => Math.max(0, Math.min(255, Math.round(v + 255 * p)));
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
}

// ---------- The peeking discount stack: front card + 1 back card ----------
function WalletStack({ data }) {
  if (!data) return null;
  // Mock a "back" card representing other saved passes
  const backData = {
    title: "Sunset Stamps",
    subtitle: "Costa Rica · 12 saved",
    color: "#f97316",
  };
  return (
    <div className="wallet-stack">
      <div
        className="wallet-card wc-back2"
        style={{ background: "linear-gradient(135deg,#16a34a,#0e6b34)" }}
      />
      <div
        className="wallet-card wc-back"
        style={{ background: "linear-gradient(135deg," + backData.color + "," + shade(backData.color, -0.25) + ")" }}
      >
        <div className="wc-bg" />
        <div className="wc-row1">
          <div className="wc-brand">CRMAPS · PASS</div>
          <div className="wc-mark">CR</div>
        </div>
      </div>
      <WalletCard data={data} peek={false} />
    </div>
  );
}

// ---------- Static OSM tile map (no iframe — safe for WebView) ----------
function OsmStaticMap({ lat, lon }) {
  const z = 16;
  const n = 1 << z;
  const tx = Math.floor((lon + 180) / 360 * n);
  const latRad = lat * Math.PI / 180;
  const tyFrac = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;
  const ty = Math.floor(tyFrac);
  const fx = ((lon + 180) / 360 * n) - tx;
  const fy = tyFrac - ty;
  const pinPctX = (1 + fx) / 3 * 100;
  const pinPctY = (1 + fy) / 3 * 100;

  const containerH = 200;
  const gridRef = useRef(null);
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    if (!gridRef.current) return;
    const gw = gridRef.current.offsetWidth;
    const pinAbsY = pinPctY / 100 * gw;
    const shift = pinAbsY - containerH / 2;
    setOffsetY(Math.max(0, Math.min(gw - containerH, shift)));
  }, [lat, lon, pinPctY]);

  const tiles = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      tiles.push({ key: `${dx},${dy}`, src: `https://tile.openstreetmap.org/${z}/${tx+dx}/${ty+dy}.png` });
    }
  }

  return (
    <div className="waze-card" style={{ overflow: 'hidden', height: containerH, position: 'relative' }}>
      <div ref={gridRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', transform: `translateY(-${offsetY}px)`, position: 'relative' }}>
        {tiles.map(({ key, src }) => (
          <img key={key} src={src} alt="" draggable={false} style={{ width: '100%', aspectRatio: '1', display: 'block' }} />
        ))}
        <div style={{ position: 'absolute', left: `${pinPctX}%`, top: `${pinPctY}%`, transform: 'translate(-50%, -100%)', fontSize: 26, lineHeight: 1, pointerEvents: 'none', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))', zIndex: 1 }}>📍</div>
        <div style={{ position: 'absolute', bottom: 2, right: 3, background: 'rgba(255,255,255,0.8)', fontSize: 9, padding: '1px 3px', borderRadius: 2, lineHeight: 1.4, zIndex: 1 }}>© OpenStreetMap</div>
      </div>
    </div>
  );
}

// ---------- DZI mini map (client's position on OUR tourist map) ----------
// Hotspot-free OSD viewer centered (zoom-in + edge magnetic) on the client's
// coordinate-generator xy. Same behaviour/settings as the index directory.
// Position comes from hs._xyParsed (offset) + hs._mapaId (map code), NOT GPS.
function DziMiniMap({ hs }) {
  const ref = useRef(null);
  const xy = hs._xyParsed ? `${hs._xyParsed.x}, ${hs._xyParsed.y}` : '';
  const mapCode = hs._mapaId || '';
  useEffect(() => {
    const el = ref.current;
    if (!el || !window.CRMapsDirMiniMap || !mapCode || !xy) return;
    const handle = window.CRMapsDirMiniMap.open(el, { mapCode, xy });
    return () => { try { handle && handle.destroy(); } catch (e) {} };
  }, [mapCode, xy]);
  return (
    <div className="waze-card" style={{ overflow: 'hidden', height: 200 }}>
      <div ref={ref} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}

// ---------- Embedded OSM map ----------
function WazePreview({ hs, useStaticTiles = false }) {
  if (!hs.gpsLat || !hs.gpsLon) return null;
  const lat = hs.gpsLat, lon = hs.gpsLon;

  if (useStaticTiles) return <OsmStaticMap lat={lat} lon={lon} />;

  const d = 0.008;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${lon-d},${lat-d},${lon+d},${lat+d}&layer=mapnik&marker=${lat},${lon}`;
  return (
    <div className="waze-card">
      <iframe
        src={src}
        width="100%"
        height="268"
        frameBorder="0"
        allowFullScreen
        title="Map"
      />
    </div>
  );
}

// ---------- «Descubre este lugar»: the place's public ficha ----------
const PLACE_DOC_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M9 13h6M9 17h4" />
  </svg>
);

// Closes the popup before leaving, so Back from the ficha (its arrow or the
// browser's) lands on the map or the directory with the popup closed:
//  - the history entry the popup pushed ({crmaps_popup}) is dropped first,
//    otherwise Back would land on it;
//  - the address we come back to loses ?client= (it would reopen the popup) and
//    keeps where the visitor was (returnParams: tab, map), for browsers that
//    reload the page on Back instead of restoring it;
//  - 'crmaps:leaving-for-ficha' lets the screen underneath (the directory) leave
//    its own state on this history entry first: the replaceState below keeps it.
function openPlaceFicha(url, onClose, returnParams) {
  const leave = () => {
    try { window.dispatchEvent(new CustomEvent('crmaps:leaving-for-ficha')); } catch (e) {}
    try {
      const here = new URL(window.location.href);
      here.searchParams.delete('client');
      const params = (typeof returnParams === 'function' ? returnParams() : returnParams) || {};
      Object.keys(params).forEach((key) => {
        if (params[key]) here.searchParams.set(key, params[key]);
        else here.searchParams.delete(key);
      });
      window.history.replaceState(window.history.state, '', here.pathname + here.search + here.hash);
    } catch (e) {}
    window.location.assign(url);
  };

  const popupEntry = !!(window.history.state && window.history.state.crmaps_popup);
  if (onClose) onClose();
  if (!popupEntry) { leave(); return; }

  // onClose() goes history.back() to drop the popup entry: leave once it lands.
  let done = false;
  const go = () => {
    if (done) return;
    done = true;
    window.removeEventListener('popstate', go);
    leave();
  };
  window.addEventListener('popstate', go);
  setTimeout(go, 600);
}

// inline: the ficha opens over the screen (PlaceFichaOverlay), so the button has
// no address to follow — not even with a long press inside the app.
function PlaceFichaButton({ url, copy, onOpen, inline = false }) {
  const content = (
    <>
      <span className="hp-place-icon" aria-hidden="true">{PLACE_DOC_ICON}</span>
      <span className="hp-place-text">
        <span className="hp-place-title">{copy.placeTitle}</span>
        <span className="hp-place-sub">{copy.placeSubtitle}</span>
      </span>
      <span className="hp-place-chev" aria-hidden="true">{Icon.chevRight}</span>
    </>
  );
  if (inline) return <button type="button" className="hp-place" onClick={onOpen}>{content}</button>;
  return <a className="hp-place" href={url} onClick={onOpen}>{content}</a>;
}

// ---------- The ficha inside the app (/phone/maps.html, /phone/directory.html) ----------
// Inside the native app, leaving for the ficha opened the whole website in the
// app (menu, footer, links to everywhere) and the visitor lost the map. There the
// ficha opens over the screen instead: ?partial=app is the ficha with its own
// stylesheet and no internal links (place.php), mounted in a shadow root so its
// styles and the app's don't mix. Closing it (✕ or Back) returns to the map or
// the directory with the popup closed. External links leave with target=_blank.
const PLACE_CLOSE_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

// One request per ficha and page load.
const placeFichaCache = new Map();

function placeFichaSrc(url) {
  try {
    const u = new URL(url, window.location.href);
    if (u.origin !== window.location.origin) return '';
    u.searchParams.set('partial', 'app');
    return u.pathname + u.search;
  } catch (e) {
    return '';
  }
}

function placeFichaId(storeName, key) {
  const generate = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  try {
    const store = window[storeName];
    let value = store.getItem(key);
    if (!value) { value = generate(); store.setItem(key, value); }
    return value;
  } catch (e) {
    return generate();
  }
}

// The same events as the ficha on the website (place.js, initTracking): a
// popup_open when it opens and an action_click per [data-action] button. The
// surface is 'ficha': on this page analytics-context.js would say 'phone', which
// belongs to the popup. pagePath still says which screen it was opened from.
function placeFichaTracker(root) {
  const el = root.querySelector('[data-crmaps-ficha-track]');
  if (!el) return null;
  const company = String(el.dataset.company || '').trim();
  const clientId = String(el.dataset.clientId || '').trim();
  const mapCode = String(el.dataset.mapCode || '').trim().toUpperCase();
  if (!company || !mapCode) return null;
  return (type, action) => {
    try {
      const attribution = typeof window.CRMapsAnalyticsContext === 'function' ? window.CRMapsAnalyticsContext() : {};
      const event = Object.assign({ type, company, clientId, mapCode }, attribution, {
        surface: 'ficha',
        userId: placeFichaId('localStorage', 'crmaps_uid'),
        sessionId: placeFichaId('sessionStorage', 'crmaps_sid'),
        ts: new Date().toISOString(),
      });
      if (action) event.action = action;
      const payload = JSON.stringify(event);
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/analytics/track', new Blob([payload], { type: 'application/json' }));
      } else {
        fetch('/api/analytics/track', { method: 'POST', body: payload, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {});
      }
    } catch (e) {}
  };
}

function PlaceFichaOverlay({ url, name, lang, onClose }) {
  const copy = HOTSPOT_COPY[lang] || HOTSPOT_COPY.en;
  const src = placeFichaSrc(url);
  const [html, setHtml] = useState(() => placeFichaCache.get(src) || '');
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const scrollRef = useRef(null);
  const hostRef = useRef(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // History. On the map the popup already has its own entry ({crmaps_popup}):
  // Back closes the popup, and the ficha goes with it. The directory's popup has
  // none, so the ficha adds one: Back (Android) closes it instead of leaving.
  useEffect(() => {
    let pushed = false;
    const onPop = () => {
      if (!pushed) return;
      pushed = false;
      onCloseRef.current();
    };
    if (!(window.history.state && window.history.state.crmaps_popup)) {
      try {
        window.history.pushState({ crmaps_ficha: true }, '');
        pushed = true;
        window.addEventListener('popstate', onPop);
      } catch (e) {}
    }
    // Escape closes the «Cómo llegar» menu first, then the ficha.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const shadow = hostRef.current && hostRef.current.shadowRoot;
      const open = shadow ? Array.from(shadow.querySelectorAll('details[data-crmaps-llegar][open]')) : [];
      if (open.length) open.forEach((menu) => menu.removeAttribute('open'));
      else onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    try { if (closeRef.current) closeRef.current.focus({ preventScroll: true }); } catch (e) {}
    return () => {
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('keydown', onKey);
      if (pushed) {
        pushed = false;
        try { window.history.back(); } catch (e) {}
      }
    };
  }, []);

  useEffect(() => {
    if (html) return undefined;
    if (!src) { setFailed(true); return undefined; }
    setFailed(false);
    let alive = true;
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = setTimeout(() => { if (ctrl) ctrl.abort(); }, 12000);
    fetch(src, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        // A whole page (menu, footer, internal links) instead of the fragment:
        // better an error than the website inside the app.
        if (/<(?:!doctype|html|head|body)[\s>]/i.test(text)) throw new Error('not a fragment');
        placeFichaCache.set(src, text);
        if (alive) setHtml(text);
      })
      .catch(() => { if (alive) setFailed(true); })
      .then(() => clearTimeout(timer));
    return () => {
      alive = false;
      clearTimeout(timer);
      if (ctrl) ctrl.abort();
    };
  }, [src, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const host = hostRef.current;
    const scroller = scrollRef.current;
    if (!html || !host || !scroller) return undefined;
    const root = host.shadowRoot || host.attachShadow({ mode: 'open' });
    root.innerHTML = html;
    scroller.scrollTop = 0;

    // place.php already leaves only external links; should an internal one ever
    // slip in, it stays without a destination rather than take the app to the website.
    root.querySelectorAll('a[href]').forEach((link) => {
      const href = String(link.getAttribute('href') || '').trim();
      if (/^(?:tel|mailto):/i.test(href)) return;
      if (/^https?:\/\//i.test(href)) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener nofollow');
        return;
      }
      link.removeAttribute('href');
    });

    const track = placeFichaTracker(root);
    if (track) track('popup_open');

    // Section tabs (.anclas): buttons that scroll inside the ficha; the one of
    // the section in view is highlighted, as initAnclas does on the website.
    const tabs = Array.from(root.querySelectorAll('.anclas [data-ancla]'));
    const setOn = (id) => tabs.forEach((tab) => tab.classList.toggle('is-on', tab.getAttribute('data-ancla') === id));
    const goTo = (id) => {
      const section = id ? root.getElementById(id) : null;
      if (!section) return;
      let smooth = true;
      try { smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
      const margin = parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
      const top = section.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - margin;
      scroller.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
      setOn(id);
      // As with a native anchor, focus follows the section: the next Tab goes into it.
      if (!section.hasAttribute('tabindex')) {
        section.setAttribute('tabindex', '-1');
        section.addEventListener('blur', () => section.removeAttribute('tabindex'), { once: true });
      }
      try { section.focus({ preventScroll: true }); } catch (e) {}
    };
    let observer = null;
    const sections = tabs.map((tab) => root.getElementById(tab.getAttribute('data-ancla'))).filter(Boolean);
    if (sections.length && typeof window.IntersectionObserver === 'function') {
      const visible = {};
      observer = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => { visible[entry.target.id] = entry.isIntersecting; });
        const current = sections.filter((section) => visible[section.id])[0];
        if (current) setOn(current.id);
      }, { root: scroller, rootMargin: '-45% 0px -50% 0px', threshold: 0 });
      sections.forEach((section) => observer.observe(section));
    }

    // «Cómo llegar» is a native <details>: closes on a click outside it or on one
    // of its options (initLlegar on the website).
    const menus = Array.from(root.querySelectorAll('details[data-crmaps-llegar]'));
    const onClick = (e) => {
      const target = e.target && e.target.closest ? e.target : null;
      if (!target) return;
      const tab = target.closest('[data-ancla]');
      if (tab) goTo(tab.getAttribute('data-ancla'));
      const action = target.closest('[data-action]');
      if (action && track) track('action_click', action.getAttribute('data-action'));
      if (menus.length) {
        let inside = target.closest('details[data-crmaps-llegar]');
        if (inside && target.closest('.llegar__menu a')) inside = null;
        menus.forEach((menu) => { if (menu !== inside) menu.removeAttribute('open'); });
      }
    };
    root.addEventListener('click', onClick);
    return () => {
      root.removeEventListener('click', onClick);
      if (observer) observer.disconnect();
    };
  }, [html]);

  return ReactDOM.createPortal(
    <div className="hp-ficha" role="dialog" aria-modal="true" aria-label={name}>
      <div className="hp-ficha-bar">
        <button type="button" ref={closeRef} className="hp-ficha-close" onClick={() => onCloseRef.current()}>
          {PLACE_CLOSE_ICON}
          <span>{copy.close}</span>
        </button>
        <span className="hp-ficha-name">{name}</span>
      </div>
      <div className="hp-ficha-scroll" ref={scrollRef}>
        {!html && (
          <div className="hp-ficha-status" role="status">
            {failed ? (
              <>
                <span>{copy.placeError}</span>
                <button type="button" className="hp-ficha-retry" onClick={() => setAttempt((n) => n + 1)}>{copy.retry}</button>
              </>
            ) : (
              <>
                <span className="hp-ficha-spinner" aria-hidden="true" />
                <span>{copy.placeLoading}</span>
              </>
            )}
          </div>
        )}
        <div ref={hostRef} className="hp-ficha-host" />
      </div>
    </div>,
    document.body
  );
}

// ---------- The full panel body (shared between variations) ----------
function PanelBody({ hs, variant, onSwitchMap, onClose, placeReturnParams, onPlaceInline, lang, showMap = true, useStaticTiles = false, useDziMap = false }) {
  const copy = HOTSPOT_COPY[lang] || HOTSPOT_COPY.en;
  const displayName = getLocalizedHotspotName(hs, lang);
  const c = hs.contact || {};
  const actions = [];
  if (c.phone) actions.push({ kind: "phone", value: c.phone });
  if (c.whatsapp) actions.push({ kind: "whatsapp", value: c.whatsapp });
  if (c.email) actions.push({ kind: "email", value: c.email });
  if (c.website) actions.push({ kind: "website", value: c.website });
  if (c.waze) actions.push({ kind: "waze", value: c.waze });
  if (c.gmaps) {
    actions.push({ kind: "gmaps", value: c.gmaps });
  } else if (hs.gpsLat && hs.gpsLon) {
    actions.push({ kind: "gmaps", value: `https://www.google.com/maps?q=${hs.gpsLat},${hs.gpsLon}` });
  }
  if (c.facebook) actions.push({ kind: "facebook", value: c.facebook });
  if (c.instagram) actions.push({ kind: "instagram", value: c.instagram });
  if (c.tripadvisor) actions.push({ kind: "tripadvisor", value: c.tripadvisor });

  const trackAction = (kind) => {
    if (window.trackEvent) {
      window.trackEvent({ type: 'action_click', company: displayName || hs.name, clientId: hs.id || '', mapCode: hs._mapaId || hs.region || '', action: kind });
    }
  };
  const descHtml = sanitizeHotspotDescriptionHtml(getLocalizedHotspotDescription(hs, lang));

  const placeUrl = (hs.placeUrls && hs.placeUrls[lang === 'es' ? 'es' : 'en']) || '';
  const openPlace = (e) => {
    trackAction('ficha');
    // Inside the app: the ficha over the screen (PlaceFichaOverlay).
    if (onPlaceInline) { onPlaceInline(); return; }
    // Ctrl/Cmd/Shift/middle click: new tab, the popup stays as it is.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    openPlaceFicha(placeUrl, onClose, placeReturnParams);
  };

  return (
    <>
      <div className="hp-body">
        {descHtml && <div className="hp-desc" dangerouslySetInnerHTML={{ __html: descHtml }} />}
        {hs.tags && hs.tags.length > 0 && (
          <div className="hp-tags">
            {hs.tags.map((t) => <span key={t} className="hp-tag">{t}</span>)}
          </div>
        )}
        <div className="hp-actions">
          {actions.map((a) => (
            <ActionBtn key={a.kind} kind={a.kind} value={a.value} primary={a.primary} lang={lang} onTrack={() => trackAction(a.kind)} />
          ))}
        </div>
        {placeUrl && <PlaceFichaButton url={placeUrl} copy={copy} onOpen={openPlace} inline={!!onPlaceInline} />}
      </div>

      {hs.discount && (
        <>
          <div className="hp-h">{copy.pass}</div>
          <WalletStack data={hs.discount} />
        </>
      )}

      {showMap && useDziMap && hs._xyParsed && hs._mapaId && (
        <>
          <div className="hp-h">{copy.mapLocation}</div>
          <DziMiniMap hs={hs} />
        </>
      )}
      {showMap && !(useDziMap && hs._xyParsed && hs._mapaId) && (hs.gpsLat && hs.gpsLon) && (
        <>
          <div className="hp-h">{copy.mapLocation}</div>
          <WazePreview hs={hs} useStaticTiles={useStaticTiles} />
        </>
      )}

      {hs.switchMapId && onSwitchMap && (
        <div className="hp-switchmap">
          <button
            className="hp-switchmap-btn"
            onClick={() => onSwitchMap(hs.switchMapId)}
          >
            {copy.openMap}
          </button>
        </div>
      )}
    </>
  );
}

// ---------- Variation A: lateral side panel ----------
// placeInline: «Descubre este lugar» opens the ficha over the screen instead of
// leaving for the website. Only the native app's pages pass it (/phone/maps.html
// and /phone/directory.html); everywhere else the button still leaves.
function SidePanel({ hs, onClose, onSwitchMap, placeReturnParams, placeInline = false, lang, showMap = true, useStaticTiles = false, useDziMap = false }) {
  const panelRef = useRef(null);
  const [imgFailed, setImgFailed] = useState(false);
  // The hotspot whose ficha is open: another hotspot, or none, closes it.
  const [placeFor, setPlaceFor] = useState(null);
  const displayName = hs ? getLocalizedHotspotName(hs, lang) : '';
  const placeUrl = (hs && hs.placeUrls && hs.placeUrls[lang === 'es' ? 'es' : 'en']) || '';
  useEffect(() => { setImgFailed(false); setPlaceFor(null); }, [hs]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    // Clear any inline styles left over from a previous swipe-close
    if (hs) {
      panel.style.transform = '';
      panel.style.transition = '';
    }

    if (!hs) return;

    let startX = 0, startY = 0, dragging = false;

    function onTouchStart(e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      dragging = false;
    }

    function onTouchMove(e) {
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!dragging) {
        if (Math.abs(dy) > Math.abs(dx)) return;
        if (dx > 8) dragging = true;
      }
      if (dragging && dx > 0) {
        e.preventDefault();
        panel.style.transition = 'none';
        panel.style.transform = `translateX(${dx}px)`;
      }
    }

    function onTouchEnd(e) {
      if (!dragging) return;
      dragging = false;
      const dx = e.changedTouches[0].clientX - startX;
      if (dx > 80) {
        panel.style.transition = 'transform 240ms ease-out';
        panel.style.transform = 'translateX(100%)';
        setTimeout(onClose, 220);
      } else {
        panel.style.transition = '';
        panel.style.transform = 'translateX(0)';
      }
    }

    panel.addEventListener('touchstart', onTouchStart, { passive: true });
    panel.addEventListener('touchmove',  onTouchMove,  { passive: false });
    panel.addEventListener('touchend',   onTouchEnd,   { passive: true });
    return () => {
      panel.removeEventListener('touchstart', onTouchStart);
      panel.removeEventListener('touchmove',  onTouchMove);
      panel.removeEventListener('touchend',   onTouchEnd);
    };
  }, [hs, onClose]);

  return (
    <>
      <div
        className={"hp-scrim" + (hs ? " is-open" : "")}
        onClick={onClose}
      />
      <aside ref={panelRef} className={"hp-panel" + (hs ? " is-open" : "")}>
        <div className="hp-scroll">
          {hs && (hs.hero && !imgFailed ? (
            <div className="hp-hero">
              <img src={hs.hero} alt={displayName} onError={() => setImgFailed(true)} />
              <button className="hp-back" onClick={onClose} aria-label="Back">{Icon.back}</button>
              <button className="hp-share" aria-label="Save">{Icon.heart}</button>
            </div>
          ) : (
            <div className="hp-noimg-bar">
              <button className="hp-back" onClick={onClose} aria-label="Back">{Icon.back}</button>
            </div>
          ))}
          {hs && (
            <div className="hp-hero-meta">
              <span className="hp-cat">
                <img src={`${window.CRMAPS_ASSET_BASE || ''}assets/icons/${hs.icon || 'general_icon.png'}`} alt="" />
                {hs.catBadge || hs.cat}
              </span>
              <h2 className="hp-name">{displayName || hs.name}</h2>
              <div className="hp-sub">{hs.region || ''}</div>
            </div>
          )}
          {hs && <PanelBody hs={hs} variant="side" onSwitchMap={onSwitchMap} onClose={onClose} placeReturnParams={placeReturnParams} onPlaceInline={placeInline ? () => setPlaceFor(hs) : undefined} lang={lang} showMap={showMap} useStaticTiles={useStaticTiles} useDziMap={useDziMap} />}
        </div>
      </aside>
      {placeInline && hs && placeFor === hs && placeUrl && (
        <PlaceFichaOverlay
          url={placeUrl}
          name={displayName || hs.name}
          lang={lang}
          onClose={() => { setPlaceFor(null); onClose(); }}
        />
      )}
    </>
  );
}

// ---------- Variation B: bottom sheet with peek ----------
function BottomSheet({ hs, onClose, onSwitchMap, placeReturnParams, lang }) {
  const [expanded, setExpanded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const displayName = hs ? getLocalizedHotspotName(hs, lang) : '';
  useEffect(() => { if (!hs) setExpanded(false); }, [hs]);
  useEffect(() => { setImgFailed(false); }, [hs]);
  if (!hs) return null;
  return (
    <>
      <div
        className={"hp-scrim" + (hs ? " is-open" : "")}
        style={{ background: expanded ? "rgba(0,0,0,0.42)" : "rgba(0,0,0,0.2)" }}
        onClick={onClose}
      />
      <section className={"hp-sheet is-open" + (expanded ? " is-expanded" : "")}>
        <div className="hp-sheet-handle" onClick={() => setExpanded((v) => !v)}>
          <span />
        </div>
        <div className="hp-scroll" style={{ maxHeight: expanded ? "none" : 480 }}>
          {hs.hero && !imgFailed ? (
            <div className="hp-hero">
              <img src={hs.hero} alt={displayName} onError={() => setImgFailed(true)} />
              <button className="hp-share" onClick={onClose} aria-label="Close" style={{ left: 14, right: "auto", top: 14, position: "absolute" }}>{Icon.back}</button>
              <button className="hp-share" aria-label="Save">{Icon.heart}</button>
            </div>
          ) : (
            <div className="hp-noimg-bar">
              <button className="hp-back" onClick={onClose} aria-label="Close">{Icon.back}</button>
            </div>
          )}
          <div className="hp-hero-meta">
            <span className="hp-cat">
              <img src={`${window.CRMAPS_ASSET_BASE || ''}assets/icons/${hs.icon || 'general_icon.png'}`} alt="" />
              {hs.catBadge || hs.cat}
            </span>
            <h2 className="hp-name">{displayName || hs.name}</h2>
            <div className="hp-sub">{hs.region || ''}</div>
          </div>
          <PanelBody hs={hs} variant="sheet" onSwitchMap={onSwitchMap} onClose={onClose} placeReturnParams={placeReturnParams} lang={lang} />
        </div>
      </section>
    </>
  );
}

function iconForCat(cat) {
  return ({
    Accommodation: "accomodation_icom.png",
    Restaurant: "restaurante_icon.png",
    Beach: "beach_icon.png",
    Park: "park_icon.png",
    Shop: "shopping_icon.png",
    Tour: "info_icon.png",
  })[cat] || "general_icon.png";
}

Object.assign(window, { SidePanel, BottomSheet, WalletStack, WazePreview, sanitizeHotspotDescriptionHtml, stripHotspotDescriptionText, getLocalizedHotspotDescription, getLocalizedHotspotName });
